import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Copy, RotateCw, Check, Coins, Lightbulb, AlertCircle, Wand2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import AdSlot from "@/components/AdSlot";

const FIELDS = [
  { key: "ai_tool", label: "AI Tool", required: true, hint: "Which model will run this prompt? E.g., ChatGPT, Claude, Gemini", type: "select",
    options: ["ChatGPT", "Claude Sonnet 4.5", "Gemini 3 Pro", "Perplexity", "Midjourney", "Other"] },
  { key: "role", label: "Role", required: true, hint: "Persona the AI should adopt. E.g., 'Senior copywriter'." },
  { key: "ask", label: "Ask", required: true, hint: "What do you want the AI to do? Be specific.", type: "textarea" },
  { key: "context", label: "Context", required: true, hint: "Background, constraints, references.", type: "textarea" },
  { key: "structure", label: "Structure", required: true, hint: "Output format. E.g., bullet list, JSON, sections." },
  { key: "methodology", label: "Methodology", required: false, hint: "Reasoning approach. E.g., Chain-of-thought, step-by-step." },
  { key: "clarity", label: "Clarity", required: false, hint: "Tone & simplicity rules. E.g., 'Plain English, no jargon'." },
  { key: "keywords", label: "Keywords", required: false, hint: "Must-include terms, comma-separated." },
  { key: "audience", label: "Audience", required: false, hint: "Who's reading? E.g., 'SaaS founders'." },
];

const DRAFT_KEY = "pf_builder_draft";

export default function PromptBuilder() {
  const { user, refresh } = useAuth();
  const [values, setValues] = useState(() => {
    try { return JSON.parse(localStorage.getItem(DRAFT_KEY)) || {}; } catch { return {}; }
  });
  const [output, setOutput] = useState("");
  const [generating, setGenerating] = useState(false);
  const [autofilling, setAutofilling] = useState(false);
  const [copied, setCopied] = useState(false);
  const [errors, setErrors] = useState({});

  // Autosave draft
  useEffect(() => {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(values));
  }, [values]);

  const setField = (k, v) => setValues((p) => ({ ...p, [k]: v }));

  const validate = () => {
    const errs = {};
    FIELDS.forEach((f) => {
      if (f.required && (!values[f.key] || !values[f.key].trim())) errs[f.key] = true;
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const generate = async () => {
    if (!validate()) {
      toast.error("Please fill all required fields (first 5)");
      return;
    }
    if ((user?.tokens_remaining || 0) < 1) {
      toast.error("Out of tokens. Top up or upgrade your plan.");
      return;
    }
    setGenerating(true);
    setOutput("");
    try {
      const { data } = await api.post("/prompts/generate", values);
      setOutput(data.prompt.output);
      await refresh();
      toast.success("Prompt crafted by Gemini Flash · 1 token used");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const copyOutput = async () => {
    await navigator.clipboard.writeText(output);
    setCopied(true);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(false), 1500);
  };

  const clearDraft = () => {
    setValues({});
    setOutput("");
    localStorage.removeItem(DRAFT_KEY);
    toast.info("Draft cleared");
  };

  const autofillOptional = async () => {
    if (!values.ask || !values.ask.trim()) {
      toast.error("Add an Ask first — I need at least that to infer the rest.");
      return;
    }
    setAutofilling(true);
    try {
      const { data } = await api.post("/prompts/autofill", {
        ai_tool: values.ai_tool || "",
        role: values.role || "",
        ask: values.ask || "",
        context: values.context || "",
        structure: values.structure || "",
      });
      setValues((p) => ({
        ...p,
        methodology: data.methodology || p.methodology || "",
        clarity: data.clarity || p.clarity || "",
        keywords: data.keywords || p.keywords || "",
        audience: data.audience || p.audience || "",
      }));
      toast.success("Optional fields auto-filled by AI");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Auto-fill failed");
    } finally {
      setAutofilling(false);
    }
  };

  return (
    <TooltipProvider delayDuration={150}>
      <div className="flex flex-col lg:flex-row lg:h-[calc(100vh-0px)] lg:overflow-hidden">
        {/* Left: 9 fields */}
        <div className="w-full lg:w-[420px] xl:w-[460px] lg:border-r border-b lg:border-b-0 border-[#E2E8F0] bg-white lg:overflow-y-auto lg:h-screen">
          <div className="p-5 sm:p-6 lg:sticky lg:top-0 bg-white border-b border-[#E2E8F0] z-10">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#14B8A6] mb-1">Prompt Builder</div>
            <h1 className="font-display text-2xl font-bold tracking-tight">5 required · 4 optional</h1>
            <p className="text-xs text-[#64748B] mt-1">Drafts autosave locally.</p>
          </div>
          <div className="p-5 sm:p-6 space-y-5">
            {FIELDS.map((f, idx) => (
              <div key={f.key}>
                {/* Divider + Autofill before first optional field */}
                {idx > 0 && !f.required && FIELDS[idx - 1].required && (
                  <div className="flex items-center gap-3 mb-5 pb-1">
                    <div className="flex-1 h-px bg-[#E2E8F0]"></div>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={autofillOptional}
                      disabled={autofilling}
                      className="border-[#14B8A6] text-[#14B8A6] hover:bg-[#14B8A6] hover:text-white gap-2 shrink-0"
                      data-testid="autofill-optional-btn"
                    >
                      <Wand2 className={`w-3.5 h-3.5 ${autofilling ? "animate-pulse" : ""}`} />
                      {autofilling ? "Thinking…" : "Auto-fill with AI"}
                    </Button>
                    <div className="flex-1 h-px bg-[#E2E8F0]"></div>
                  </div>
                )}
                <div className="flex items-center gap-2 mb-1.5">
                  <Label htmlFor={f.key} className="text-sm font-semibold text-[#111827]">
                    {f.label}
                    {f.required ? (
                      <span className="ml-1 text-[#EF4444]">*</span>
                    ) : (
                      <span className="ml-2 text-[10px] font-medium uppercase tracking-wider text-[#64748B] bg-[#F5F7FA] px-1.5 py-0.5 rounded">Optional</span>
                    )}
                  </Label>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button type="button" className="text-[#64748B] hover:text-[#1F3A8A]" data-testid={`hint-${f.key}`}>
                        <Lightbulb className="w-3.5 h-3.5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-xs">{f.hint}</TooltipContent>
                  </Tooltip>
                  {errors[f.key] && <AlertCircle className="w-3.5 h-3.5 text-[#EF4444]" />}
                </div>
                {f.type === "textarea" ? (
                  <Textarea
                    id={f.key}
                    data-testid={`field-${f.key}`}
                    rows={3}
                    value={values[f.key] || ""}
                    onChange={(e) => setField(f.key, e.target.value)}
                    placeholder={f.hint}
                    className={errors[f.key] ? "border-[#EF4444]" : ""}
                  />
                ) : f.type === "select" ? (
                  <Select value={values[f.key] || ""} onValueChange={(v) => setField(f.key, v)}>
                    <SelectTrigger data-testid={`field-${f.key}`} className={errors[f.key] ? "border-[#EF4444]" : ""}>
                      <SelectValue placeholder="Choose an AI tool" />
                    </SelectTrigger>
                    <SelectContent>
                      {f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    id={f.key}
                    data-testid={`field-${f.key}`}
                    value={values[f.key] || ""}
                    onChange={(e) => setField(f.key, e.target.value)}
                    placeholder={f.hint}
                    className={errors[f.key] ? "border-[#EF4444]" : ""}
                  />
                )}
              </div>
            ))}
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={clearDraft} data-testid="clear-draft-btn">Clear</Button>
            </div>
            <AdSlot variant="card" className="mt-4" />
          </div>
        </div>

        {/* Right: output */}
        <div className="flex-1 flex flex-col bg-[#F5F7FA] lg:overflow-hidden min-h-[60vh] lg:min-h-0">
          <div className="p-4 sm:p-6 border-b border-[#E2E8F0] bg-white flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#64748B]">Output</div>
              <motion.div
                key={user?.tokens_remaining}
                initial={{ scale: 1.15 }}
                animate={{ scale: 1 }}
                className="flex items-center gap-1.5 px-2.5 py-1 bg-[#1F3A8A]/5 border border-[#1F3A8A]/20 rounded text-sm font-semibold text-[#1F3A8A]"
                data-testid="builder-token-balance"
              >
                <Coins className="w-3.5 h-3.5" /> {user?.tokens_remaining ?? 0} tokens
              </motion.div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {output && (
                <>
                  <Button variant="outline" size="sm" onClick={copyOutput} data-testid="copy-output-btn" className="gap-2">
                    {copied ? <Check className="w-4 h-4 text-[#14B8A6]" /> : <Copy className="w-4 h-4" />}
                    <span className="hidden sm:inline">{copied ? "Copied" : "Copy"}</span>
                  </Button>
                  <Button variant="outline" size="sm" onClick={generate} disabled={generating} data-testid="regenerate-btn" className="gap-2">
                    <RotateCw className="w-4 h-4" /> <span className="hidden sm:inline">Re-generate</span>
                  </Button>
                </>
              )}
              <Button onClick={generate} disabled={generating} className="bg-[#14B8A6] hover:bg-[#0D9488] text-white btn-lift gap-2" data-testid="generate-prompt-btn">
                <Sparkles className="w-4 h-4" />
                <span className="whitespace-nowrap">{generating ? "Gemini is thinking…" : "Generate (1 token)"}</span>
              </Button>
            </div>
          </div>

          <div className="flex-1 lg:overflow-y-auto p-5 sm:p-8 lg:p-12">
            <AnimatePresence mode="wait">
              {generating ? (
                <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="max-w-3xl space-y-3">
                  {[...Array(8)].map((_, i) => (
                    <div key={i} className="h-4 bg-white border border-[#E2E8F0] rounded animate-pulse" style={{ width: `${60 + Math.random() * 40}%` }}></div>
                  ))}
                </motion.div>
              ) : output ? (
                <motion.div
                  key="output"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="max-w-3xl"
                  data-testid="prompt-output"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#64748B]">Generated prompt</div>
                    <div className="flex items-center gap-1.5 px-2 py-0.5 rounded bg-[#14B8A6]/10 border border-[#14B8A6]/20 text-[10px] font-semibold text-[#0D9488] uppercase tracking-wider" data-testid="prompt-model-badge">
                      <Sparkles className="w-3 h-3" /> Crafted by Gemini Flash
                    </div>
                  </div>
                  <pre className="whitespace-pre-wrap break-words font-mono text-sm sm:text-[15px] leading-relaxed text-[#111827] bg-white border border-[#E2E8F0] rounded-md p-5 sm:p-8 shadow-sm">{output}</pre>
                </motion.div>
              ) : (
                <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-md mx-auto text-center py-12 sm:py-24">
                  <div className="w-16 h-16 rounded-md bg-white border border-[#E2E8F0] flex items-center justify-center mx-auto mb-6">
                    <Sparkles className="w-7 h-7 text-[#14B8A6]" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-display text-xl sm:text-2xl font-bold mb-2">Your prompt will appear here.</h3>
                  <p className="text-[#64748B] text-sm sm:text-base">Fill the 5 required fields (and optionally add 4 more refiners), hit Generate, and Gemini Flash will craft a precision prompt for {values.ai_tool || "your chosen AI tool"}.</p>
                  {(user?.tokens_remaining || 0) < 1 && (
                    <Link to="/plans">
                      <Button className="mt-6 bg-[#1F3A8A] hover:bg-[#1E40AF] text-white">Top up tokens</Button>
                    </Link>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
